/**
 * Os ajustes da casa (`GET /settings`).
 *
 * Vem uma vez por sessão e fica: são as chaves de recurso e o desenho da casa,
 * que mudam quando alguém mexe nos ajustes e não a cada tela. Quem mexe chama
 * `recarregarAjustes`.
 */

import { useEffect, useState } from 'react';
import { ligacao } from './ligacao';

/**
 * Um recurso do catálogo (`FEATURE_CATALOG` em `server/src/settings.js`).
 *
 * O catálogo vem do servidor e não daqui porque é ele que decide o que existe:
 * um interruptor escrito no site para uma chave que o servidor não conhece
 * seria descartado no `saveSettings` sem ninguém entender por quê.
 */
export interface RecursoDoCatalogo {
  id: string;
  label: string;
  /** O título do grupo onde o interruptor aparece: "Assistir", "Cozinha"... */
  group: string;
  /** Se nasce ligado. O site não usa: `ligado()` já sabe os dois que não. */
  on: boolean;
}

export interface Ajustes {
  /**
   * Se quem pediu manda na casa.
   *
   * Não é o slug de `casa.lider`: o servidor já faz a comparação (`liderDaCasa`
   * em `casa.js`) e manda só a resposta, `true` ou `false`. É o `isLeader` do
   * `app_settings.dart`.
   */
  lider: boolean;
  /** Recurso ligado ou desligado. O que não está aqui vale como ligado. */
  features: Record<string, boolean>;
  layout: {
    /** `YYYY-MM-DD` do dia em que começou. */
    startDate?: string;
    heroStyle?: string;
    density?: string;
    accent?: string;
  };
  /** Slug -> id da paleta. É de cada um, e não da casa. */
  themes: Record<string, string>;
  /** Slug -> e-mail ou índice da conta do YouTube Music. Vazio desliga. */
  music: Record<string, string>;
  /** Slug -> nome de usuário no Last.fm. */
  lastfm: Record<string, string>;
  lastfmKey: string;
  catalog: RecursoDoCatalogo[];
}

const VAZIO: Ajustes = {
  lider: false,
  features: {},
  layout: {},
  themes: {},
  music: {},
  lastfm: {},
  lastfmKey: '',
  catalog: [],
};

let pedido: Promise<Ajustes> | null = null;
const atentos = new Set<(a: Ajustes) => void>();
let guardado: Ajustes | null = null;

function buscar(): Promise<Ajustes> {
  pedido ??= ligacao()
    .api.ajustes<Ajustes>()
    .then((a) => {
      guardado = {
        ...VAZIO,
        ...a,
        layout: { ...a.layout },
        // Um servidor mais velho não manda catálogo, e a tela de recursos
        // percorre esta lista: `null` ali derrubaria a tela inteira.
        catalog: Array.isArray(a.catalog) ? a.catalog : [],
      };
      for (const f of atentos) f(guardado);
      return guardado;
    })
    .catch(() => {
      // Sem ajustes o app ainda abre, só com os padrões. Zera o pedido para a
      // próxima tela tentar de novo em vez de herdar a falha para sempre.
      pedido = null;
      return VAZIO;
    });
  return pedido;
}

export async function recarregarAjustes(): Promise<Ajustes> {
  pedido = null;
  return buscar();
}

export function usarAjustes(): Ajustes {
  const [a, setA] = useState<Ajustes>(() => guardado ?? VAZIO);

  useEffect(() => {
    let vivo = true;
    atentos.add(setA);
    void buscar().then((novo) => {
      if (vivo) setA(novo);
    });
    return () => {
      vivo = false;
      atentos.delete(setA);
    };
  }, []);

  return a;
}

/** O dia em que começou. O padrão é o do Flutter, para os dois contarem igual. */
export function inicioDaCasa(a: Ajustes): Date {
  const bruto = a.layout.startDate;
  if (bruto) {
    // Os ajustes guardam `2021-12-31`, sem hora. O `DateTime.tryParse` do Dart
    // lê isso como meia-noite local; o `new Date` do JavaScript lê como
    // meia-noite em Greenwich, e aqui, três horas atrás, isso volta um dia — o
    // contador mostrava 30 de dezembro. Data sem hora é montada à mão.
    const so = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bruto.trim());
    const d = so ? new Date(+so[1], +so[2] - 1, +so[3]) : new Date(bruto);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date(2021, 11, 31);
}

/**
 * Os dois que nascem desligados: o `_shippedOff` do `app_settings.dart`.
 *
 * Anime é um catálogo que a casa pode nem querer, e finanças pede uma
 * credencial de banco que só uma pessoa tem. Quem quiser, liga nos ajustes.
 */
const NASCEM_DESLIGADOS = new Set(['anime', 'financas']);

/**
 * Um recurso desligado some da navegação. O que não foi dito está ligado,
 * menos os dois que o Flutter entrega desligados — sem isso a casa nova
 * mostrava no site duas telas que o celular escondia.
 */
export const ligado = (a: Ajustes, chave: string): boolean =>
  a.features[chave] ?? !NASCEM_DESLIGADOS.has(chave);

/**
 * O catálogo em grupos, na ordem em que os recursos chegaram.
 *
 * A tela de recursos escreve um título por grupo, e a ordem dos títulos é a da
 * primeira aparição — a mesma coisa que o `_agrupar` do Flutter faz com um
 * `LinkedHashMap`, que preserva a ordem de inserção.
 */
export function recursosPorGrupo(a: Ajustes): Array<[string, RecursoDoCatalogo[]]> {
  const grupos = new Map<string, RecursoDoCatalogo[]>();
  for (const r of a.catalog) {
    const nome = r.group || 'Outros';
    const lista = grupos.get(nome);
    if (lista) lista.push(r);
    else grupos.set(nome, [r]);
  }
  return [...grupos];
}

/** O tema de alguém. Vazio enquanto os ajustes não chegaram. */
export const temaDe = (a: Ajustes, slug?: string | null): string =>
  (slug && a.themes[slug]) || '';

/**
 * O link que troca a conta ativa do YouTube Music neste navegador — o
 * `youtubeMusicAccountUrl` de `lib/core/music/youtube_music.dart`.
 *
 * `N` é a posição da conta no seletor do Google, contando do zero. O caminho
 * `/u/N/` é a única coisa que o YouTube Music aceita: o `authuser` na query ele
 * descarta, e um e-mail no lugar do número cai na tela de senha. Por isso quem
 * guardou e-mail não ganha o botão — o endereço vale como etiqueta, e nada mais.
 */
export function enderecoDaContaDeMusica(conta?: string | null): string | null {
  const limpa = (conta ?? '').trim();
  return /^[0-9]$/.test(limpa) ? `https://music.youtube.com/u/${limpa}/` : null;
}

/**
 * Quem já usa esta cor, se não for a própria pessoa.
 *
 * A exclusividade é a regra que a casa pediu: uma captura de tela tem de dizer
 * de quem é sem precisar de legenda. O servidor recusa a troca; a tela mostra
 * o cadeado antes de alguém tentar.
 */
export function donoDoTema(a: Ajustes, id: string, slug?: string | null): string | null {
  for (const [outro, escolhido] of Object.entries(a.themes)) {
    if (escolhido === id && outro !== slug) return outro;
  }
  return null;
}
